const WISHLIST_STORAGE_KEY = 'rosfo:wishlist';
const WISHLIST_CHANGE_EVENT = 'rosfo:wishlist-change';
const WISHLIST_REFRESH_TTL = 24 * 60 * 60 * 1000;

function normalizeWishlistItem(item) {
  if (!item?.id || !item?.handle) return null;

  const normalized = {
    id: String(item.id),
    handle: String(item.handle),
  };

  if (typeof item.title === 'string') normalized.title = item.title;
  if (typeof item.url === 'string') normalized.url = item.url;
  if (typeof item.image === 'string') normalized.image = item.image;

  const price = Number(item.price);
  if (Number.isFinite(price)) normalized.price = price;

  if (typeof item.available === 'boolean') normalized.available = item.available;
  if (typeof item.currency === 'string') normalized.currency = item.currency;
  if (Number.isFinite(Number(item.updatedAt))) normalized.updatedAt = Number(item.updatedAt);

  return normalized;
}

function hasWishlistSnapshot(item) {
  return (
    typeof item.title === 'string' &&
    item.title.length > 0 &&
    typeof item.url === 'string' &&
    item.url.length > 0 &&
    Number.isFinite(Number(item.price)) &&
    typeof item.available === 'boolean'
  );
}

function shouldRefreshWishlistItem(item, currency, now = Date.now()) {
  return (
    !hasWishlistSnapshot(item) ||
    item.currency !== currency ||
    !Number.isFinite(Number(item.updatedAt)) ||
    now - Number(item.updatedAt) > WISHLIST_REFRESH_TTL
  );
}

function getWishlist() {
  try {
    const storedItems = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');
    if (!Array.isArray(storedItems)) return [];

    const uniqueItems = new Map();
    for (const item of storedItems) {
      const normalizedItem = normalizeWishlistItem(item);
      if (!normalizedItem) continue;
      uniqueItems.set(normalizedItem.id, normalizedItem);
    }
    return Array.from(uniqueItems.values());
  } catch (_error) {
    return [];
  }
}

function saveWishlist(
  items,
  {
    notify = true,
    action = null,
  } = {}
) {
  const uniqueItems = new Map();
  for (const item of items) {
    const normalizedItem = normalizeWishlistItem(item);
    if (!normalizedItem) continue;
    uniqueItems.set(normalizedItem.id, normalizedItem);
  }
  const normalizedItems = Array.from(uniqueItems.values());

  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(normalizedItems));
  } catch (_error) {
    return false;
  }

  if (notify) {
    window.dispatchEvent(
      new CustomEvent(
        WISHLIST_CHANGE_EVENT,
        {
          detail: {
            items: normalizedItems,
            action,
          },
        }
      )
    );
  }
  return true;
}

class RosfoWishlistButton extends HTMLElement {
  feedbackAnimation;
  flight;
  headerAnimation;

  connectedCallback() {
    this.button = this.querySelector('[data-wishlist-toggle]');
    this.button?.addEventListener('click', this.handleClick);
    window.addEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    window.addEventListener('storage', this.handleStorage);
    this.updateState();
    this.refreshStoredSnapshot();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    window.removeEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    window.removeEventListener('storage', this.handleStorage);

    this.feedbackAnimation?.cancel();
    this.cancelFlight();
    this.headerAnimation?.cancel();
  }

  handleClick = () => {
    const items = getWishlist();
    const productId = String(this.dataset.productId);
    const existingIndex = items.findIndex(
      (item) => item.id === productId
    );

    const isAdding = existingIndex < 0;

    if (isAdding) {
      items.push(this.getSnapshot());
    } else {
      items.splice(existingIndex, 1);
    }

    if (
      !saveWishlist(
        items,
        {
          action: isAdding ? 'add' : 'remove',
        }
      )
    ) {
      return;
    }

    const feedback = this.querySelector('[data-wishlist-feedback]');
    if (feedback) feedback.textContent = `${this.dataset.i18nWishlist}: ${items.length}`;
    this.animateAddedFeedback(isAdding);
    this.cancelFlight();
    if (isAdding) this.animateFlyToWishlist();
  };

  cancelFlight() {
    if (!this.flight) return;
    this.flight.controller.abort();
    this.flight.animation.cancel();
    this.flight.node.remove();
    this.flight = undefined;
  }

  animateFlyToWishlist() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const source = this.querySelector('[data-wishlist-icon]');
    const destination = Array.from(document.querySelectorAll('.rosfo-wishlist__header-link')).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });
    const svg = source?.querySelector('svg');
    if (!source || !destination || !svg) return;
    const from = source.getBoundingClientRect();
    const to = destination.getBoundingClientRect();
    if (from.bottom < 0 || from.top > window.innerHeight) return;
    const node = document.createElement('span');
    node.className = 'rosfo-wishlist-flight';
    node.setAttribute('aria-hidden', 'true');
    Object.assign(node.style, {
      position: 'fixed', left: `${from.left + from.width / 2 - 12}px`, top: `${from.top + from.height / 2 - 12}px`,
      width: '24px', height: '24px', zIndex: '10000', pointerEvents: 'none', color: '#111',
    });
    const star = svg.cloneNode(true);
    star.style.cssText = 'width:24px;height:24px;display:block;overflow:visible';
    star.querySelector('path')?.setAttribute('fill', 'currentColor');
    node.appendChild(star);
    document.body.appendChild(node);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const animation = node.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * 0.45}px,${dy * 0.55 - 40}px) scale(0.85)`, opacity: 0.95, offset: 0.55 },
      { transform: `translate(${dx}px,${dy}px) scale(0.45)`, opacity: 0.2 },
    ], { duration: 520, easing: 'cubic-bezier(.2,.65,.35,1)' });
    const controller = new AbortController();
    this.flight = { node, animation, controller };
    // Scrolling or resizing invalidates the measured destination; never leave a floating star behind.
    window.addEventListener('scroll', () => this.cancelFlight(), { capture: true, passive: true, once: true, signal: controller.signal });
    window.addEventListener('resize', () => this.cancelFlight(), { once: true, signal: controller.signal });
    animation.finished.then(() => {
      controller.abort();
      node.remove();
      if (this.flight?.node === node) this.flight = undefined;
      if (destination.isConnected && this.button?.getAttribute('aria-pressed') === 'true') {
        this.headerAnimation?.cancel();
        this.headerAnimation = destination.animate([
          { transform: 'scale(1)' }, { transform: 'scale(1.16)', offset: 0.4 }, { transform: 'scale(1)' },
        ], { duration: 240, easing: 'ease-out' });
      }
    }, () => { controller.abort(); node.remove(); });
  }

  animateAddedFeedback(isAdding) {
    this.feedbackAnimation?.cancel();
    const icon = this.querySelector('[data-wishlist-icon]');
    if (!icon || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.feedbackAnimation = icon.animate(
      isAdding
        ? [{ transform: 'scale(1)' }, { transform: 'scale(1.2)', offset: 0.45 }, { transform: 'scale(1)' }]
        : [{ transform: 'scale(1)' }, { transform: 'scale(0.85)', offset: 0.45 }, { transform: 'scale(1)' }],
      { duration: 280, easing: 'ease-out' }
    );
  }

  handleChange = () => this.updateState();

  handleStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key === null) this.updateState();
  };

  getSnapshot() {
    return {
      id: String(this.dataset.productId),
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '',
      image: this.dataset.productImage || '',
      price: Number(this.dataset.productPrice || 0),
      available: this.dataset.productAvailable === 'true',
      currency: this.dataset.productCurrency || '',
      updatedAt: Date.now(),
    };
  }

  refreshStoredSnapshot() {
    const items = getWishlist();
    const productId = String(this.dataset.productId);
    const index = items.findIndex((item) => item.id === productId);

    if (index < 0) return;

    const currentItem = items[index];
    const currentSnapshot = this.getSnapshot();
    const snapshotFields = ['title', 'url', 'image', 'price', 'available', 'currency', 'handle'];
    const hasChanged = snapshotFields.some((field) => currentItem[field] !== currentSnapshot[field]);

    if (!hasChanged) return;

    items[index] = {
      ...currentItem,
      ...currentSnapshot,
      updatedAt: Date.now(),
    };
    saveWishlist(items);
  }

  updateState() {
    const isSaved = getWishlist().some((item) => item.id === String(this.dataset.productId));
    const productTitle = this.dataset.productTitle || '';
    const action = isSaved ? this.dataset.i18nRemove : this.dataset.i18nAdd;

    if (this.button) {
      this.button.setAttribute('aria-pressed', String(isSaved));
      this.button.setAttribute('aria-label', `${action} ${productTitle}: ${this.dataset.i18nWishlist}`);
      this.button.title = `${action}: ${this.dataset.i18nWishlist}`;
    }
  }
}

class RosfoWishlistCount extends HTMLElement {
  countAnimationTimeout;

  connectedCallback() {
    this.count =
      this.querySelector('[data-wishlist-count]');

    this.countText =
      this.querySelector('[data-wishlist-count-text]');

    this.link = this.querySelector('a');

    window.addEventListener(
      WISHLIST_CHANGE_EVENT,
      this.handleChange
    );

    window.addEventListener(
      'storage',
      this.handleStorage
    );

    this.count?.addEventListener(
      'animationend',
      this.handleCountAnimationEnd
    );

    this.updateCount(false);
  }

  disconnectedCallback() {
    window.removeEventListener(
      WISHLIST_CHANGE_EVENT,
      this.handleChange
    );

    window.removeEventListener(
      'storage',
      this.handleStorage
    );

    this.count?.removeEventListener(
      'animationend',
      this.handleCountAnimationEnd
    );

    if (this.countAnimationTimeout) {
      window.clearTimeout(
        this.countAnimationTimeout
      );
    }
  }

  handleChange = (event) => {
    this.updateCount(event.detail?.action === 'add' || event.detail?.action === 'remove');
  };

  handleStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key === null) {
      this.updateCount(true);
    }
  };

  handleCountAnimationEnd = (event) => {
    if (event.target !== this.count) return;

    this.count?.classList.remove(
      'rosfo-wishlist-count--animating'
    );
  };

  updateCount(animate = false) {
    const count = getWishlist().length;

    if (this.count) {
      this.count.hidden = count === 0;
    }

    if (this.countText) {
      this.countText.textContent =
        String(count);
    }

    if (
      animate &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      count > 0 &&
      this.count
    ) {
      this.count.classList.remove(
        'rosfo-wishlist-count--animating'
      );

      void this.count.offsetWidth;

      this.count.classList.add(
        'rosfo-wishlist-count--animating'
      );
    }

    const countLabel =
      count === 1
        ? this.dataset.i18nItem
        : this.dataset.i18nItems;

    this.link?.setAttribute(
      'aria-label',
      `${this.dataset.i18nWishlist}, ${count} ${countLabel}`
    );
  }
}

class RosfoWishlistPage extends HTMLElement {
  renderVersion = 0;

  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.status = this.querySelector('[data-wishlist-status]');
    this.addEventListener('click', this.handleClick);
    window.addEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    window.addEventListener('storage', this.handleStorage);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    window.removeEventListener('storage', this.handleStorage);
  }

  handleClick = (event) => {
    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (!removeButton || !this.contains(removeButton)) return;

    const items = getWishlist().filter((item) => item.id !== removeButton.dataset.wishlistRemove);
    saveWishlist(items);
  };

  handleChange = () => this.render();

  handleStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key === null) this.render();
  };

  async render() {
    const version = ++this.renderVersion;
    const items = getWishlist();

    if (!this.grid || !this.emptyState) return;

    if (items.length === 0) {
      this.grid.replaceChildren();
      this.emptyState.hidden = false;
      this.setStatus('');
      return;
    }

    const initialCards = items.map((product) => this.createProductCard(product));
    this.grid.replaceChildren(...initialCards);
    this.emptyState.hidden = true;

    const itemsToRefresh = items.filter((item) =>
      shouldRefreshWishlistItem(item, this.dataset.currency)
    );

    if (itemsToRefresh.length === 0) {
      this.setStatus('');
      return;
    }

    this.setStatus(this.dataset.i18nLoading);

    const refreshedItems = await this.refreshWishlistItems(itemsToRefresh);

    if (version !== this.renderVersion) return;

    const refreshedById = new Map(
      refreshedItems.map((item) => [String(item.id), item])
    );

    const latestItems = getWishlist();

    const products = latestItems.map((item) => {
      return refreshedById.get(String(item.id)) || item;
    });

    if (JSON.stringify(latestItems) !== JSON.stringify(products)) {
      saveWishlist(products, { notify: false });
    }

    if (version !== this.renderVersion) return;

    const refreshedCards = products.map((product) =>
      this.createProductCard(product)
    );

    this.grid.replaceChildren(...refreshedCards);
    this.emptyState.hidden = refreshedCards.length > 0;
    this.setStatus('');
  }

  async refreshWishlistItems(items) {
    const queue = [...items];
    const migrated = [];

    const worker = async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) return;

        try {
          migrated.push(await this.fetchProductSnapshot(item));
        } catch (_error) {
          // Keep the latest stored item unchanged when the refresh request fails.
        }
      }
    };

    const workerCount = Math.min(3, queue.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    return migrated;
  }

  async fetchProductSnapshot(savedItem) {
    const response = await fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(savedItem.handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Unable to load ${savedItem.handle}`);

    const product = await response.json();
    const fallbackUrl = `${this.dataset.productsRoot}${encodeURIComponent(savedItem.handle)}`;
    const imageUrl =
      typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src;

    return {
      id: String(savedItem.id),
      handle: savedItem.handle,
      title: product.title,
      url: product.url || fallbackUrl,
      image: imageUrl || '',
      price: Number(product.price || 0),
      available: Boolean(product.available),
      currency: this.dataset.currency,
      updatedAt: Date.now(),
    };
  }

  createProductCard(product) {
    const card = document.createElement('article');
    card.className = 'rosfo-wishlist__card';

    const productUrl = product.url || `${this.dataset.productsRoot}${encodeURIComponent(product.handle)}`;
    const productTitle = product.title || product.handle;

    const mediaLink = document.createElement('a');
    mediaLink.className = 'rosfo-wishlist__media';
    mediaLink.href = productUrl;

    if (product.image) {
      const image = document.createElement('img');
      image.className = 'rosfo-wishlist__image';
      image.src = product.image;
      image.alt = productTitle;
      image.loading = 'lazy';
      mediaLink.append(image);
    }

    const title = document.createElement('a');
    title.className = 'rosfo-wishlist__title';
    title.href = productUrl;
    title.textContent = productTitle;

    const details = [mediaLink, title];

    if (Number.isFinite(Number(product.price))) {
      const price = document.createElement('p');
      price.className = 'rosfo-wishlist__price';
      price.textContent = this.formatMoney(product.price, product.currency || this.dataset.currency);
      details.push(price);
    }

    if (typeof product.available === 'boolean') {
      const availability = document.createElement('p');
      availability.className = 'rosfo-wishlist__availability';
      availability.textContent = product.available ? this.dataset.i18nAvailable : this.dataset.i18nSoldOut;
      details.push(availability);
    }

    const actions = document.createElement('div');
    actions.className = 'rosfo-wishlist__actions';

    const viewLink = document.createElement('a');
    viewLink.className = 'rosfo-wishlist__view';
    viewLink.href = productUrl;
    viewLink.textContent = this.dataset.i18nViewProduct;

    const removeButton = document.createElement('button');
    removeButton.className = 'button button-unstyled rosfo-wishlist__remove';
    removeButton.type = 'button';
    removeButton.dataset.wishlistRemove = product.id;
    removeButton.textContent = this.dataset.i18nRemove;
    removeButton.setAttribute('aria-label', `${this.dataset.i18nRemove} ${productTitle}: ${this.dataset.i18nWishlist}`);

    actions.append(viewLink, removeButton);
    card.append(...details, actions);
    return card;
  }

  formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('rosfo-wishlist-button')) {
  customElements.define('rosfo-wishlist-button', RosfoWishlistButton);
}

if (!customElements.get('rosfo-wishlist-count')) {
  customElements.define('rosfo-wishlist-count', RosfoWishlistCount);
}

if (!customElements.get('rosfo-wishlist-page')) {
  customElements.define('rosfo-wishlist-page', RosfoWishlistPage);
}
